// «Сон»: последняя ночь, средняя длительность, график длительности и фаз по ночам.

import { useState } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { PageHeader } from '@/components/layout'
import { Card, EmptyState, ErrorText, PageSpinner, Stat, Tabs } from '@/components/ui'
import { formatDate, formatDayMonth, formatDuration, formatNumber, formatTime } from '@/lib/format'
import { periodRange, type Period } from '@/lib/periods'

import { useSleep } from './api'
import { PHASE_LABELS, averageSleep, sleepChartRows, sleepPhases } from './logic'

const PERIODS = [
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: '3m', label: '3 мес' },
  { value: 'year', label: 'Год' },
] as const satisfies readonly { value: Period; label: string }[]

const PHASES = ['deep', 'light', 'rem', 'awake'] as const

// Лёгкий — тот же бирюзовый, но полупрозрачный: --accent-soft на тёмном фоне почти не видно.
const PHASE_COLORS = {
  deep: 'var(--accent-strong)',
  light: 'color-mix(in srgb, var(--accent) 40%, transparent)',
  rem: 'var(--chart-2)',
  awake: 'var(--warning)',
} as const

export function SleepPage() {
  const [period, setPeriod] = useState<Period>('month')
  const range = periodRange(period)
  const sleep = useSleep(range.start, range.end)
  const nights = sleep.data ?? []
  const last = nights.length ? [...nights].sort((a, b) => b.day.localeCompare(a.day))[0]! : null
  const average = averageSleep(nights)
  const rows = sleepChartRows(nights)

  return (
    <>
      <PageHeader title="Сон" description="Ночи из Garmin: длительность, фазы и оценка. День ночи — день пробуждения." />
      {sleep.isPending ? (
        <PageSpinner />
      ) : sleep.error ? (
        <Card>
          <ErrorText error={sleep.error} />
        </Card>
      ) : (
        <>
          <Card>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat
                label="Последняя ночь"
                value={last ? formatDuration(last.duration_s) : '—'}
                hint={
                  last
                    ? `${formatDate(last.day)}${last.start_at && last.end_at ? ` · ${formatTime(last.start_at)}–${formatTime(last.end_at)}` : ''}`
                    : undefined
                }
              />
              <Stat label="Оценка" value={last?.score ?? '—'} unit={last?.score != null ? 'из 100' : undefined} />
              <Stat label="В среднем" value={formatDuration(average)} hint={`ночей: ${nights.length}`} />
              {/* На телефоне фазы во всю ширину: в половине карточки «Бодрствование» не помещается. */}
              <div className="col-span-2 min-w-0 sm:col-span-1">
                <div className="text-xs font-medium uppercase tracking-wide text-muted">Фазы</div>
                <ul className="mt-1 space-y-0.5 text-sm tabular-nums">
                  {last && sleepPhases(last).length ? (
                    sleepPhases(last).map((p) => (
                      <li key={p.key} className="flex justify-between gap-2">
                        <span className="text-muted">{p.label}</span>
                        <span className="whitespace-nowrap text-fg">{formatDuration(p.seconds)}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-muted">нет данных</li>
                  )}
                </ul>
              </div>
            </div>
          </Card>
          <Card
            className="mt-4"
            title="Длительность и фазы"
            actions={<Tabs items={PERIODS} value={period} onChange={setPeriod} size="sm" label="Период" />}
          >
            {rows.length === 0 ? (
              <EmptyState icon="chart" title="Ночей за период нет" description="Сон приходит из Garmin после синхронизации." />
            ) : (
              <>
                <div className="h-72" role="img" aria-label="Сон по ночам">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                      <CartesianGrid stroke="var(--border)" vertical={false} />
                      <XAxis dataKey="day" tickFormatter={(d: string) => formatDayMonth(d)} tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                      <YAxis unit=" ч" tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                      <Tooltip
                        labelFormatter={(d) => formatDate(String(d))}
                        formatter={(value, name) => [`${formatNumber(Number(value))} ч`, String(name)]}
                        contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}
                      />
                      {PHASES.map((key) => (
                        <Bar
                          key={key}
                          dataKey={key}
                          name={PHASE_LABELS[key]}
                          stackId="sleep"
                          fill={PHASE_COLORS[key]}
                          isAnimationActive={false}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Своя легенда: порядок фаз как в стопке, на узком экране переносится целыми пунктами. */}
                <ul className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted">
                  {PHASES.map((key) => (
                    <li key={key} className="flex items-center gap-1.5">
                      <span className="size-2.5 rounded-sm" style={{ background: PHASE_COLORS[key] }} aria-hidden />
                      {PHASE_LABELS[key]}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        </>
      )}
    </>
  )
}
